import { useCallback, Dispatch, SetStateAction } from 'react';
import {
  findFieldCoordinates,
  calculateBoundingBoxCenter,
  calculateCentroidFromFeatures,
  calculateFitZoom
} from '../helpers';

interface UseTreeSelectionProps {
  selectedKeys: string[];
  setSelectedKeys: (keys: string[]) => void;
  setExpandedKeys: Dispatch<SetStateAction<string[]>>;
  setManualCoords: (coords: [number, number] | null) => void;
  setZoom: (zoom: number[]) => void;
  geoJSONFeatures: any[];
  onDeselect: () => void;
}

/**
 * Custom hook to manage tree node selection and map navigation
 * Handles selection state, expansion, and map coordinate updates
 */
const useTreeSelection = ({
  selectedKeys,
  setSelectedKeys,
  setExpandedKeys,
  setManualCoords,
  setZoom,
  geoJSONFeatures,
  onDeselect
}: UseTreeSelectionProps) => {
  /**
   * Toggle expansion state for a tree node
   */
  const toggleExpansion = useCallback(
    (key: string) => {
      setExpandedKeys(prev => {
        if (prev.includes(key)) {
          return prev.filter(k => k !== key);
        }
        return [...prev, key];
      });
    },
    [setExpandedKeys]
  );

  /**
   * Update selection keys with tree key and entity ID
   * Automatically deduplicates when they're the same (BY_FARM mode)
   */
  const updateSelection = useCallback(
    (treeKey: string, entityId: string) => {
      const newKeys = Array.from(new Set([treeKey, entityId]));
      setSelectedKeys(newKeys);
    },
    [setSelectedKeys]
  );

  /**
   * Handle field selection and navigation
   */
  const handleFieldClick = useCallback(
    (fieldId: string, fieldKey: string) => {
      const isAlreadySelected = selectedKeys.includes(fieldKey);

      toggleExpansion(fieldKey);

      if (isAlreadySelected) {
        onDeselect();
      } else {
        updateSelection(fieldKey, fieldId);
        const coordinates = findFieldCoordinates(geoJSONFeatures, fieldId);
        if (coordinates) {
          setManualCoords(coordinates);
          // Compute zoom from the field's own bbox so the view tightly
          // wraps the field instead of snapping to a fixed FIELD constant
          // that's often too far out for typical field sizes.
          const fieldFeature = geoJSONFeatures.find(
            f => f?.properties?.id === fieldId
          );
          setZoom([
            calculateFitZoom(fieldFeature ? [fieldFeature] : geoJSONFeatures)
          ]);
        }
      }
    },
    [
      selectedKeys,
      onDeselect,
      updateSelection,
      toggleExpansion,
      geoJSONFeatures,
      setManualCoords,
      setZoom
    ]
  );

  /**
   * Handle farm selection and navigation
   */
  const handleFarmClick = useCallback(
    (farmId: string, farmKey: string) => {
      const isAlreadySelected = selectedKeys.includes(farmKey);

      toggleExpansion(farmKey);

      if (isAlreadySelected) {
        onDeselect();
      } else {
        updateSelection(farmKey, farmId);
        const farmFeatures = geoJSONFeatures.filter(
          f => f?.properties?.farmId === farmId
        );
        // Center + zoom with the same helpers the open fit-all view uses, so
        // for a single-farm record the target equals the open camera and the
        // map doesn't re-fly (no zoom nudge); with multiple farms it frames
        // just the clicked farm. See CWFWEB-9716.
        const coordinates = calculateBoundingBoxCenter(farmFeatures);
        if (coordinates) {
          setManualCoords(coordinates);
          setZoom([calculateFitZoom(farmFeatures)]);
        }
      }
    },
    [
      selectedKeys,
      onDeselect,
      updateSelection,
      toggleExpansion,
      geoJSONFeatures,
      setManualCoords,
      setZoom
    ]
  );

  /**
   * Handle crop selection - selects all crop zones within the crop
   */
  const handleCropClick = useCallback(
    (cropId: string, cropZoneIds: string[]) => {
      const isAlreadySelected = selectedKeys.includes(cropId);

      toggleExpansion(cropId);

      if (isAlreadySelected) {
        onDeselect();
      } else {
        setSelectedKeys([cropId, ...cropZoneIds]);

        const selectedFeatures = geoJSONFeatures.filter(feature =>
          feature.properties?.cropZones?.some((cz: any) =>
            cropZoneIds.includes(cz.cropZoneId)
          )
        );

        if (selectedFeatures.length > 0) {
          const centroid = calculateCentroidFromFeatures(selectedFeatures);
          if (centroid) {
            setManualCoords(centroid);
            // Compute zoom from the bbox of the crop's selected fields so
            // the view actually fits them, instead of snapping to the
            // DEFAULT constant.
            setZoom([calculateFitZoom(selectedFeatures)]);
          }
        }
      }
    },
    [
      selectedKeys,
      onDeselect,
      setSelectedKeys,
      toggleExpansion,
      geoJSONFeatures,
      setManualCoords,
      setZoom
    ]
  );

  return {
    handleFieldClick,
    handleFarmClick,
    handleCropClick
  };
};

export default useTreeSelection;
